import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  TextField,
  InputAdornment,
  Toolbar,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
} from "@mui/material";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
  SearchOutlined,
} from "@mui/icons-material";
import { isNil } from "ramda";
import moment from "moment";
import { capitalize } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { Data } from "../../../store/audit/types";
import AuditDetail from "../auditDetail/AuditDetail";
import { fetchRequest } from "../../../store/audit/actions";
import ProgressIndicator from "../../../common/progressIndicator/ProgressIndicator";
import EntityIdentifierDialog from "./ListEntityIdentifier";
import { styled, useTheme } from "@mui/system";

interface RowProps {
  row: Data;
}

const EntityType = {
  merchant: "MERCHANT",
  terminal: "TERMINAL",
  csTerminal: "CSTERMINAL",
  pos: "POS",
  posSoftware: "POSSOFTWARE",
  processor: "PROCESSOR",
  user: "USER",
  userGroup: "USERGROUP",
  cardPrefix: "CARD",
  cardPrompt: "CARDPROMPTS",
  scheduleJob: "SCHEDULEJOB",
  group: "GROUP",
  receipt: "RECEIPT",
  dmgOnboarding: "DMG-ONBOARDING",
  TERMINALBACKGROUND: "TERMINALBACKGROUND",
  csBranch: "BRANCH",
  csWorkStation: "WORKSTATION",
};

interface TablePaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: Function;
}

class AuditTableParams {
  page = 0;
  pageSize = 10;
  pageCount = 0;
  orderBy = -1;
  orderDirection = "";
  searchText = "";
  searchField = "attributes.EntityName";
  totalCount = 1;
}

interface Pagination {
  page: {
    limit: string;
    offset: string;
  };
  filter: { [index: string]: string };
  sort: string;
}

const Row = (props: RowProps) => {
  const { row } = props;
  const [open, setOpen] = useState(false);
  const [openDialog, setOpenDialog] = React.useState(false);

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
  };

  const rowCss = {
    showMore: {
      cursor: "pointer",
      color: "var(--dmg-purple)",
    },
    root: {
      "& > *": {
        borderBottom: "unset",
        fontSize: "inherit",
      },
    },
  };

  const showMore = () => {
    setOpen(!open);
  };

  const renderCollapsibleBox = (row: Data) => {
    return <AuditDetail row={row} />;
  };

  const renderAuditIdentifier = (row: Data) => {
    if (row.attributes.ChangeType === "bulkUpdate") {
      return (
        <Box sx={rowCss.showMore} onClick={handleClickOpen}>
          Show Updated {capitalize(row.attributes.EntityName)}s
        </Box>
      );
    }
    switch (row.attributes.EntityName) {
      case EntityType.merchant:
        return `${row.attributes.Data?.To?.attributes?.MID}`;
      case EntityType.csTerminal:
      case EntityType.terminal:
        return `${row.attributes.Data?.To?.attributes?.TID}`;
      case EntityType.pos:
        return `${row.attributes.Data?.To?.attributes?.POSDesc}`;
      case EntityType.posSoftware:
        return `${row.attributes.Data?.To?.attributes?.ApplicationName}`;
      case EntityType.processor:
        return `${row.attributes.Data?.To?.attributes?.Name}`;
      case EntityType.user:
        return `${row.attributes.Data?.To?.attributes?.FirstName} ${row.attributes.Data?.To?.attributes?.LastName}`;
      case EntityType.userGroup:
        return `${
          row.attributes.ChangeType !== "delete"
            ? row.attributes.Data?.To?.attributes?.Name
            : row.attributes.Data?.From?.attributes?.Name
        }`;
      case EntityType.cardPrefix:
        return `${row.attributes.Data?.To?.attributes?.Name}`;
      case EntityType.cardPrompt:
        return `${row.attributes.Data?.To?.attributes?.Name}`;
      case EntityType.scheduleJob:
        return `${row.attributes.Data?.To?.parameters.MID}`;
      case EntityType.group:
        return `${row.attributes.Data?.To?.name}`;
      case EntityType.receipt:
        return `${
          row.attributes.ChangeType !== "delete"
            ? row.attributes.Data?.To?.attributes?.Name
            : row.attributes.Data?.From?.attributes?.Name
        }`;
      case EntityType.TERMINALBACKGROUND:
        return `${
          row.attributes.ChangeType !== "delete"
            ? row.attributes.Data?.To?.attributes?.FileName
            : row.attributes.Data?.From?.attributes?.FileName
        }`;
      case EntityType.dmgOnboarding:
        return `${row.attributes.Data?.To?.attributes?.OnboardingID}`;
      case EntityType.csBranch:
        return `${row.attributes.Data?.To?.attributes?.Name}`;
      case EntityType.csWorkStation:
        return `${row.attributes.Data?.To?.attributes?.WorkstationId}`;
      default:
        return "";
    }
  };

  const renderEntityType = (EntityName: string) => {
    if (EntityName === "USERGROUP") {
      return "ROLE";
    }
    return EntityName;
  };

  return (
    <>
      <TableRow sx={rowCss.root}>
        <TableCell width={250}>
          {!isNil(row.createdAt)
            ? moment(row.createdAt)
                .local()
                .format("DD/MM/YYYY HH:mm:ss A")
            : ""}
        </TableCell>
        <TableCell width={200}>{row.attributes.ChangeType}</TableCell>
        <TableCell width={200}>
          {renderEntityType(row.attributes.EntityName)}
        </TableCell>
        <TableCell width={200}>{renderAuditIdentifier(row)}</TableCell>
        <TableCell width={200}>{row.attributes.ChangedBy?.Name}</TableCell>
        <TableCell width={200}>
          {row.dataDetail && (
            <Box sx={rowCss.showMore} onClick={showMore}>
              {open ? "Show less" : "Show more"}
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            borderBottom: open ? "1px solid rgba(224, 224, 224, 1)" : "unset",
          }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            {renderCollapsibleBox(row)}
          </Collapse>
        </TableCell>
      </TableRow>
      {row.attributes.ChangeType === "bulkUpdate" && (
        <EntityIdentifierDialog
          entityIdentifier={row.attributes.EntityIdentifier}
          open={openDialog}
          entityName={row.attributes.EntityName}
          onClose={handleClose}
        />
      )}
    </>
  );
};

const TablePaginationActions = (props: TablePaginationProps) => {
  const classes = {
    root: {
      flexShrink: 0,
      marginLeft: 2.5,
    },
  };

  const { count, page, rowsPerPage, onPageChange } = props;
  const theme = useTheme();

  const handleFirstPageButtonClick = (event: any) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event: any) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event: any) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event: any) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  const firstTooltip = "First Page";
  const previousTooltip = "Previous Page";
  const nextTooltip = "Next Page";
  const lastTooltip = "Last Page";
  const labelDisplayedRows = "{from}-{to} of {count}";

  return (
    <Box sx={classes.root}>
      <Tooltip title={firstTooltip}>
        <IconButton
          onClick={handleFirstPageButtonClick}
          disabled={page === 0}
          aria-label="first page"
        >
          {theme.direction === "rtl" ? <LastPage /> : <FirstPage />}
        </IconButton>
      </Tooltip>
      <Tooltip title={previousTooltip}>
        <IconButton
          onClick={handleBackButtonClick}
          disabled={page === 0}
          aria-label="previous page"
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowRight />
          ) : (
            <KeyboardArrowLeft />
          )}
        </IconButton>
      </Tooltip>
      <Typography
        variant="caption"
        style={{
          flex: 1,
          textAlign: "center",
          alignSelf: "center",
          flexBasis: "inherit",
        }}
      >
        {labelDisplayedRows
          .replace(
            "{from}",
            (count === 0 ? 0 : page * rowsPerPage + 1).toString()
          )
          .replace("{to}", Math.min((page + 1) * rowsPerPage, count).toString())
          .replace("{count}", count.toString())}
      </Typography>
      <Tooltip title={nextTooltip}>
        <IconButton
          onClick={handleNextButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="next page"
        >
          {theme.direction === "rtl" ? (
            <KeyboardArrowLeft />
          ) : (
            <KeyboardArrowRight />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title={lastTooltip}>
        <IconButton
          onClick={handleLastPageButtonClick}
          disabled={page >= Math.ceil(count / rowsPerPage) - 1}
          aria-label="last page"
        >
          {theme.direction === "rtl" ? <FirstPage /> : <LastPage />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default function AuditTable(): React.ReactElement {
  const [params, setParams] = useState<AuditTableParams>(
    new AuditTableParams()
  );
  const dispatch = useDispatch();
  const auditState = useSelector((state: ApplicationState) => state.audit);
  const audit = auditState.data?.data;
  const paginationAudit = auditState.data?.pagination;

  const [isFirstTimeLoad, setFirstTimeLoad] = useState(true);

  const refreshData = (newParams: AuditTableParams) => {
    const p: Pagination = {
      page: {
        limit: String(newParams.pageSize),
        offset: String(newParams.pageSize * newParams.page),
      },
      filter: {},
      sort: "",
    };

    if (newParams.searchText) {
      p.filter[newParams.searchField] = (newParams.searchText || "").trim();
      if (
        newParams.searchField === "attributes.EntityName" &&
        newParams.searchText.toUpperCase() === "ROLE"
      ) {
        p.filter[newParams.searchField] = "USERGROUP";
      }
    }
    setParams({ ...newParams });

    dispatch(fetchRequest(null, p));
  };

  const onSearchChange = () => {
    const p = { ...params, page: 0 };
    refreshData(p);
  };

  const handleChangePage = (event: any, newPage: number) => {
    const p = { ...params, page: newPage };
    refreshData(p);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const p = {
      ...params,
      pageSize: parseInt(event.target.value, 10),
      page: 0,
    };
    refreshData(p);
  };

  const handleOnChange = (event: { target: { value: any } }) => {
    setParams({
      ...params,
      searchText: event.target.value,
    });
  };

  const handleOnKeyPress = (event: any) => {
    if (event.keyCode == 13) {
      onSearchChange();
    }
  };

  useEffect(() => {
    if (!isFirstTimeLoad) {
      const timeoutId = setTimeout(() => {
        onSearchChange();
      }, 800);
      return () => clearTimeout(timeoutId);
    } else {
      onSearchChange();
      setFirstTimeLoad(false);
    }
  }, []);

  const tableCss = {
    tablePagination: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
    },

    select: {
      marginRight: "5px",
    },
    spacer: {
      flex: "1 1 10%",
    },
  };

  const StyledTable = styled("table")({
    width: "100%",
    "& > tbody > tr > td.MuiTableCell-root": {
      border: "unset",
    },
  });

  return (
    <>
      <Toolbar>
        <Box sx={tableCss.spacer} />
        <Select
          variant="standard"
          value={params.searchField}
          sx={tableCss.select}
          onChange={(event: SelectChangeEvent<string>) => {
            setParams({
              ...params,
              searchField: event.target.value,
            });
          }}
        >
          <MenuItem value="attributes.EntityName" selected={true}>
            Entity Type
          </MenuItem>
          <MenuItem value="attributes.EntityIdentifier" selected={true}>
            Entity Identifier
          </MenuItem>
          <MenuItem value="attributes.ChangeType">Change</MenuItem>
          <MenuItem value="attributes.ChangedBy">User</MenuItem>
        </Select>
        <TextField
          placeholder="Search"
          onChange={handleOnChange}
          value={params.searchText}
          onKeyDown={handleOnKeyPress}
          variant="standard"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="medium" onClick={onSearchChange}>
                  <SearchOutlined fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Toolbar>
      {isNil(audit) ? (
        <ProgressIndicator />
      ) : (
        <>
          <TableContainer>
            <Table aria-label="collapsible table">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Change</TableCell>
                  <TableCell>Entity Type</TableCell>
                  <TableCell style={{ minWidth: "220px" }}>
                    Entity Identifier
                  </TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditState.data?.data?.map((row) => (
                  <Row key={row.id} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <StyledTable>
            <tbody>
              <tr>
                <TablePagination
                  rowsPerPageOptions={[
                    { value: 5, label: "5 rows" },
                    { value: 10, label: "10 rows" },
                    { value: 20, label: "20 rows" },
                  ]}
                  count={paginationAudit?.total || 0}
                  colSpan={3}
                  rowsPerPage={paginationAudit?.limit || 10}
                  page={params.page}
                  sx={tableCss.tablePagination}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={""}
                  labelDisplayedRows={({ from, to, count }) => ``}
                  ActionsComponent={TablePaginationActions}
                />
              </tr>
            </tbody>
          </StyledTable>
        </>
      )}
    </>
  );
}
